<template>
  <Dialog
    title="个人中心"
    :visible.sync="dialogVisible"
    width="560px"
    :close-on-click-modal="false"
    @open="handleOpen"
  >
    <el-tabs v-model="activeTab">
      <el-tab-pane label="基本资料" name="profile">
        <el-descriptions
          v-loading="profileLoading"
          :column="1"
          border
          size="small"
        >
          <el-descriptions-item label="用户名">
            {{ profileForm.userName || "-" }}
          </el-descriptions-item>
          <el-descriptions-item label="姓名">
            {{ profileForm.nickName || "-" }}
          </el-descriptions-item>
          <el-descriptions-item label="手机号">
            {{ profileForm.phonenumber || "-" }}
          </el-descriptions-item>
          <el-descriptions-item label="角色">
            {{ profileForm.roleGroup || "-" }}
          </el-descriptions-item>
          <el-descriptions-item label="创建时间">
            {{ profileForm.createTime || "-" }}
          </el-descriptions-item>
          <el-descriptions-item label="最后登录">
            {{ loginDateText }}
          </el-descriptions-item>
          <el-descriptions-item label="登录IP">
            {{ profileForm.loginIp || "-" }}
          </el-descriptions-item>
        </el-descriptions>
      </el-tab-pane>

      <el-tab-pane label="修改密码" name="password">
        <el-form
          ref="pwdRef"
          :model="pwdForm"
          :rules="pwdRules"
          label-width="100px"
        >
          <el-form-item label="新密码" prop="newPassword">
            <el-input
              v-model.trim="pwdForm.newPassword"
              type="password"
              show-password
              placeholder="6~14位，仅数字/英文/标点"
            />
          </el-form-item>
          <el-form-item label="确认密码" prop="confirmPassword">
            <el-input
              v-model.trim="pwdForm.confirmPassword"
              type="password"
              show-password
              placeholder="请再次输入新密码"
            />
          </el-form-item>
        </el-form>
        <div class="profile-footer">
          <el-button
            plain
            round
            size="small"
            type="primary"
            :loading="pwdSaving"
            @click="savePassword"
            >修改密码</el-button
          >
        </div>
      </el-tab-pane>
    </el-tabs>
  </Dialog>
</template>

<script>
import { teacherAuthInfo } from "@/api/teacher/auth";
import { updateTeacher } from "@/api/teacher";
import { validatePassword } from "@/utils/validate";
import {
  setUserInfo,
  getUserInfo,
  clearUserInfo,
  normalizeTeacherAuthInfo
} from "@/utils/userInfo";
import { clearDictCache } from "@/utils/dict";
import { clearCachedMenus } from "@/utils/dynamicMenu";
import { resetDynamicRoutes } from "@/utils/dynamicRoutes";
import { clearUserPermissions } from "@/utils/permission";
import { formatDateTime } from "@/utils/util";
import { clearToken } from "@/utils/token";

const defaultProfileForm = () => ({
  userName: "",
  nickName: "",
  phonenumber: "",
  email: "",
  sex: "0",
  roleGroup: "",
  createTime: "",
  loginDate: "",
  loginIp: "",
  teacherId: null
});

const defaultPwdForm = () => ({
  newPassword: "",
  confirmPassword: ""
});

const SEX_MAP = {
  "0": "男",
  "1": "女",
  "2": "未知"
};

export default {
  data() {
    const validateConfirmPassword = (rule, value, callback) => {
      if (!value) {
        callback(new Error("请再次输入新密码"));
        return;
      }
      if (value !== this.pwdForm.newPassword) {
        callback(new Error("两次输入的密码不一致"));
        return;
      }
      callback();
    };
    return {
      dialogVisible: false,
      activeTab: "profile",
      profileLoading: false,
      pwdSaving: false,
      profileForm: defaultProfileForm(),
      pwdForm: defaultPwdForm(),
      pwdRules: {
        newPassword: [
          { required: true, message: "请输入新密码", trigger: "blur" },
          { validator: validatePassword, trigger: "blur" }
        ],
        confirmPassword: [
          {
            required: true,
            validator: validateConfirmPassword,
            trigger: "blur"
          }
        ]
      }
    };
  },
  computed: {
    sexLabel() {
      return SEX_MAP[this.profileForm.sex] || "未知";
    },
    loginDateText() {
      return formatDateTime(this.profileForm.loginDate) || "-";
    }
  },
  methods: {
    open() {
      this.activeTab = "profile";
      this.dialogVisible = true;
    },
    handleOpen() {
      this.pwdForm = defaultPwdForm();
      this.$nextTick(() => {
        this.$refs.pwdRef && this.$refs.pwdRef.clearValidate();
      });
      this.loadProfile();
    },
    async loadProfile() {
      this.profileLoading = true;
      try {
        const result = await teacherAuthInfo();
        const data = normalizeTeacherAuthInfo(result.data || {});
        const roleNames = (data.roles || [])
          .map(r => (typeof r === "string" ? r : r.roleName || r.roleKey))
          .filter(Boolean)
          .join("、");
        this.profileForm = {
          userName: data.userName || data.account || "",
          nickName: data.nickName || data.teacherName || "",
          phonenumber: data.phonenumber || data.mobile || "",
          email: data.email || "",
          sex: data.sex != null ? String(data.sex) : "0",
          roleGroup: data.roleGroup || roleNames || "",
          createTime: data.createTime || "",
          loginDate: data.loginDate || "",
          loginIp: data.loginIp || "",
          teacherId: data.teacherId != null ? data.teacherId : data.id
        };
        setUserInfo(data);
        this.$emit("profile-loaded", data);
        this.$root.Bus.$emit("user-info-updated");
      } catch (e) {
        this.dialogVisible = false;
      } finally {
        this.profileLoading = false;
      }
    },
    async savePassword() {
      if (this.pwdSaving) return;
      try {
        await this.$refs.pwdRef.validate();
      } catch (e) {
        return;
      }
      const cached = getUserInfo() || {};
      const teacherId =
        this.profileForm.teacherId != null
          ? this.profileForm.teacherId
          : cached.teacherId != null
          ? cached.teacherId
          : cached.id;
      if (teacherId == null || teacherId === "") {
        this.$message.warning("无法获取教师信息，请重新登录后再修改密码");
        return;
      }
      this.pwdSaving = true;
      try {
        // education-teacher：PUT /teacher/teacher，只传 teacherId + password
        await updateTeacher({
          teacherId,
          password: this.pwdForm.newPassword
        });
        this.$message.success("密码修改成功，请重新登录");
        this.dialogVisible = false;
        this.pwdForm = defaultPwdForm();
        setTimeout(() => {
          clearToken();
          clearUserInfo();
          clearDictCache();
          clearCachedMenus();
          clearUserPermissions();
          resetDynamicRoutes();
          this.$router.push({ path: "/login" });
        }, 800);
      } catch (e) {
      } finally {
        this.pwdSaving = false;
      }
    }
  }
};
</script>

<style scoped>
.profile-footer {
  text-align: right;
  padding-top: 8px;
}
</style>



// WEBPACK FOOTER //
// src/views/Layout/ProfileCenter.vue